import { useState, useEffect, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { generateToken, AGORA_APP_ID } from '../services/agoraToken';
import { onSnapshot, doc } from 'firebase/firestore';
import { db } from '../services/firebase';

export default function CallScreen({ agent, coins, onEnd, onEmergency }) {
  const [seconds, setSeconds] = useState(0);
  const [currentCoins, setCurrentCoins] = useState(coins);
  const [muted, setMuted] = useState(false);
  const [status, setStatus] = useState('calling');
  const timerRef = useRef(null);
  const secondsRef = useRef(0);
  const clientRef = useRef(null);
  const localTrackRef = useRef(null);

useEffect(() => {
    startCall();

    // Auto cancel if no response in 30 seconds
    const cancelTimeout = setTimeout(() => {
      cleanup();
      alert('No response from agent. Please try again.');
      onEnd(0);
    }, 30000);

    if (agent.sessionId) {
      const unsubscribe = onSnapshot(doc(db, 'sessions', agent.sessionId), (snap) => {
        if (snap.exists()) {
          const sessionStatus = snap.data().status;
          setStatus(sessionStatus);

          if (sessionStatus === 'ongoing' && !timerRef.current) {
            clearTimeout(cancelTimeout);
            timerRef.current = setInterval(() => {
              secondsRef.current += 1;
              setSeconds(secondsRef.current);
              setCurrentCoins(c => {
                const newCoins = Math.max(0, c - agent.ratePerSecond);
                if (newCoins <= 0) {
                  clearInterval(timerRef.current);
                  setTimeout(() => onEnd(-1), 100);
                  return 0;
                }
                return newCoins;
              });
            }, 1000);
          }

          if (sessionStatus === 'completed' || sessionStatus === 'emergency') {
            clearTimeout(cancelTimeout);
            cleanup();
            onEnd(secondsRef.current);
          }

          if (sessionStatus === 'declined') {
            clearTimeout(cancelTimeout);
            cleanup();
            alert('Agent is busy right now. Please try again or call another agent.');
            onEnd(0);
          }
        }
      });
      return () => { cleanup(); unsubscribe(); clearTimeout(cancelTimeout); };
    }

    return () => { cleanup(); clearTimeout(cancelTimeout); };
  }, []);

  const startCall = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const channelName = agent.channelName || "test123";
      const token = await generateToken(channelName);

      clientRef.current = AgoraRTC.createClient({
        mode: "rtc", codec: "vp8"
      });

      const client = clientRef.current;

      await client.join(AGORA_APP_ID, channelName, token, null);

      localTrackRef.current = await AgoraRTC.createMicrophoneAudioTrack();
      await client.publish(localTrackRef.current);

      client.on("user-published", async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === "audio") {
          user.audioTrack.play();
        }
      });

    } catch (error) {
      console.error("Agora error:", error.message);
    }
  };

  const cleanup = async () => {
    clearInterval(timerRef.current);
    if (localTrackRef.current) {
      localTrackRef.current.close();
    }
    if (clientRef.current) {
      await clientRef.current.leave();
    }
  };

  const handleEnd = async () => {
    clearInterval(timerRef.current);
    await cleanup();
    if (agent.sessionId) {
      const { updateDoc, doc: firestoreDoc } = await import('firebase/firestore');
      const { db: firestoreDb } = await import('../services/firebase');
      await updateDoc(firestoreDoc(firestoreDb, 'sessions', agent.sessionId), {
        status: 'completed',
        duration: secondsRef.current,
      });
    }
    onEnd(secondsRef.current);
  };

  const handleEmergency = async () => {
    clearInterval(timerRef.current);
    await cleanup();
    if (agent.sessionId) {
      const { updateDoc, doc: firestoreDoc } = await import('firebase/firestore');
      const { db: firestoreDb } = await import('../services/firebase');
      await updateDoc(firestoreDoc(firestoreDb, 'sessions', agent.sessionId), {
        status: 'emergency',
      });
    }
    onEmergency();
  };

  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const coinsPercent = Math.max(0, (currentCoins / coins) * 100);

  return (
    <div style={styles.container}>
      <div style={styles.liveBadge}>
        {status === 'calling' ? '⏳ WAITING FOR AGENT...' : '● LIVE SESSION'}
      </div>

      <div style={styles.avatarContainer}>
        <div style={styles.avatar}>{agent.avatar}</div>
        {status === 'ongoing' && !muted && <div style={styles.pulse}></div>}
      </div>

      <div style={styles.agentName}>{agent.codename}</div>
      <div style={styles.agentSpec}>{agent.specialty}</div>

      <div style={styles.timerBox}>
        <div style={styles.timer}>{fmt(seconds)}</div>
        <div style={styles.coinRate}>
          {status === 'calling' ? 'Connecting...' : `🪙 ${agent.ratePerSecond} coins/sec`}
        </div>
      </div>

      <div style={styles.progressContainer}>
        <div style={styles.progressBar}>
          <div style={{
            ...styles.progressFill,
            width: `${coinsPercent}%`,
            backgroundColor: coinsPercent > 30 ? '#22c55e' : '#ef4444',
          }}></div>
        </div>
        <div style={styles.coinsRemaining}>
          🪙 {currentCoins.toLocaleString()} remaining
        </div>
      </div>

      <div style={styles.waveContainer}>
        {[...Array(8)].map((_, i) => (
          <div key={i} style={{
            ...styles.waveBar,
            height: status === 'ongoing'
              ? `${20 + Math.sin(i * 0.8 + seconds * 0.5) * 15}px`
              : '4px',
            opacity: muted ? 0.2 : 0.7,
          }}></div>
        ))}
      </div>

      <div style={styles.controls}>
        <button
          style={{
            ...styles.muteBtn,
            backgroundColor: muted ? '#ef4444' : '#ffffff15',
          }}
          onClick={async () => {
            if (localTrackRef.current) {
              await localTrackRef.current.setMuted(!muted);
            }
            setMuted(!muted);
          }}
        >
          {muted ? '🔇' : '🔊'}
        </button>
        <button style={styles.endBtn} onClick={handleEnd}>
          {status === 'calling' ? 'Cancel' : 'End Session'}
        </button>
      </div>

      <button style={styles.emergencyBtn} onClick={handleEmergency}>
        🛑 Emergency Stop
      </button>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.8); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0f0f1a',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 24px 24px',
    fontFamily: 'sans-serif',
  },
  liveBadge: {
    color: '#22c55e',
    fontWeight: '700',
    fontSize: '12px',
    letterSpacing: '2px',
    marginBottom: '24px',
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: '16px',
  },
  avatar: {
    width: '100px',
    height: '100px',
    borderRadius: '28px',
    backgroundColor: '#2d1b69',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    border: '3px solid #6366f1',
    boxShadow: '0 0 30px #6366f140',
    position: 'relative',
    zIndex: 1,
  },
  pulse: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    borderRadius: '28px',
    border: '3px solid #6366f1',
    animation: 'pulse 1.5s ease-out infinite',
  },
  agentName: {
    color: '#ffffff',
    fontWeight: '900',
    fontSize: '22px',
    marginBottom: '4px',
  },
  agentSpec: {
    color: '#6366f1',
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '24px',
  },
  timerBox: {
    backgroundColor: '#ffffff08',
    borderRadius: '16px',
    padding: '16px 32px',
    textAlign: 'center',
    marginBottom: '20px',
  },
  timer: {
    color: '#ffffff',
    fontSize: '48px',
    fontWeight: '900',
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '2px',
  },
  coinRate: {
    color: '#ffffff50',
    fontSize: '13px',
    marginTop: '4px',
  },
  progressContainer: {
    width: '100%',
    maxWidth: '400px',
    marginBottom: '24px',
  },
  progressBar: {
    backgroundColor: '#ffffff15',
    borderRadius: '4px',
    height: '6px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 1s linear, background-color 0.3s',
  },
  coinsRemaining: {
    color: '#ffffff60',
    fontSize: '13px',
    textAlign: 'center',
  },
  waveContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    height: '60px',
    marginBottom: '32px',
  },
  waveBar: {
    width: '4px',
    borderRadius: '2px',
    backgroundColor: '#6366f1',
    transition: 'height 0.3s ease',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    width: '100%',
    maxWidth: '400px',
    marginBottom: '12px',
  },
  muteBtn: {
    width: '56px',
    height: '56px',
    borderRadius: '18px',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  endBtn: {
    flex: 1,
    backgroundColor: '#ef4444',
    border: 'none',
    borderRadius: '18px',
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '800',
    cursor: 'pointer',
  },
  emergencyBtn: {
    backgroundColor: '#ef444415',
    border: '1px solid #ef444450',
    borderRadius: '14px',
    padding: '14px 28px',
    color: '#ef4444',
    fontWeight: '800',
    fontSize: '14px',
    cursor: 'pointer',
    width: '100%',
    maxWidth: '400px',
  },
};